import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Card, Select, Skeleton, Tag, Typography, message } from "antd";

import {
  traineeAiReportModelOptions,
  getTraineeAiReportModelOption,
  type TraineeAiReportModelOptionId,
} from "@fitness-app/data-models/entities/TraineeAiReportConfig";
import { AppConfigType, type TraineeAiReportAppConfig } from "@fitness-app/data-models/entities/TrainerConfig";
import { type Json } from "@fitness-app/supabase";

import { supabase } from "~/store/initializeStore";

// Model raportu AI o klientce (TraineeProfile) — app_config typu trainee_ai_report,
// rozwiązywany serwerowo przez task generate-trainee-ai-report. Lustro
// AdvisorModelCard: ten sam katalog modeli, zapis RLS-owo tylko dla konta
// właściciela (TRAINER).

const queryKey = ["app_config", AppConfigType.TRAINEE_AI_REPORT];

export const TraineeAiReportModelCard = (): React.ReactElement => {
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select()
        .eq("type", AppConfigType.TRAINEE_AI_REPORT)
        .maybeSingle<TraineeAiReportAppConfig>();

      if (error) throw error;

      return data;
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: async (modelOptionId: TraineeAiReportModelOptionId) => {
      const config = configQuery.data;
      if (!config?.id) {
        throw new Error("Brak rekordu konfiguracji raportu AI (app_config, typ trainee_ai_report).");
      }

      const { data, error } = await supabase
        .from("app_config")
        .update({
          data: { ...config.data, modelOptionId } as unknown as Json,
          updatedAt: new Date().toISOString(),
          version: (config.version ?? 0) + 1,
        })
        .eq("id", config.id)
        .eq("version", config.version ?? 0)
        .select()
        .maybeSingle<TraineeAiReportAppConfig>();

      if (error) throw error;
      if (!data) {
        await queryClient.invalidateQueries({ queryKey });
        throw new Error("Konfiguracja została zmieniona w innym miejscu — odśwież i spróbuj ponownie.");
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      void message.success("Konfiguracja raportu AI zapisana.");
    },
    onError: (error) => {
      void message.error(
        error instanceof Error
          ? error.message
          : "Nie udało się zapisać konfiguracji (zmiana wymaga konta właściciela).",
      );
    },
  });

  return (
    <Card size="small" title="Raport AI o klientce" style={{ marginBottom: 16 }}>
      {configQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : configQuery.isError || !configQuery.data ? (
        <Alert
          type="warning"
          showIcon
          message="Brak konfiguracji raportu AI"
          description="Nie znaleziono rekordu app_config typu trainee_ai_report — do czasu jego dodania raporty generują się na modelu domyślnym."
        />
      ) : (
        <>
          <Typography.Text strong>Model</Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Select<TraineeAiReportModelOptionId>
              style={{ width: 320 }}
              value={getTraineeAiReportModelOption(configQuery.data.data.modelOptionId).id}
              loading={updateConfigMutation.isLoading}
              disabled={updateConfigMutation.isLoading}
              onChange={(modelOptionId) => updateConfigMutation.mutate(modelOptionId)}
              options={traineeAiReportModelOptions.map((option) => ({
                value: option.id,
                label: (
                  <span>
                    {option.label}
                    {option.reasoningEffort ? <Tag style={{ marginLeft: 4 }}>{option.reasoningEffort}</Tag> : null}
                  </span>
                ),
              }))}
            />
          </div>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 8 }}>
            Model używany do raportów AI generowanych z profilu klientki (dla trenera i dla klientki). Zmiana zapisuje
            się od razu; zmienić może tylko konto właściciela.
          </Typography.Paragraph>
        </>
      )}
    </Card>
  );
};
